import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Bot, 
  CheckCircle2, 
  Loader2, 
  Clock, 
  Wrench, 
  Search, 
  Sparkles, 
  ChevronDown, 
  ChevronUp, 
  ShieldCheck, 
  ShoppingCart, 
  Bell
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Language, Vibe } from '../types';
import { NutriAgentLaunchConfig } from './NutriAgentConfigModal';

interface NutriAgentProgressProps {
  isActive: boolean;
  config: NutriAgentLaunchConfig | null;
  onComplete: () => void;
  onCancel: () => void;
  lang?: Language;
  vibe?: Vibe | 'casual' | 'professional';
  theme?: 'light' | 'dark';
  skin?: 'default' | 'cyberpunk' | 'anime' | 'stalker';
  illumination?: boolean;
}

interface TimelineAction {
  id: string;
  type: 'thought' | 'skill' | 'search' | 'verification';
  title?: string;
  thought: string;
  details?: string;
  badge?: string;
  toolName?: string;
}

const getTimelineActions = (
  config: NutriAgentLaunchConfig | null,
  lang: Language = 'en-US',
  vibe: Vibe | 'casual' | 'professional' = 'casual',
  skin: 'default' | 'cyberpunk' | 'anime' | 'stalker' = 'default'
): TimelineAction[] => {
  const storeName = config?.targetStores?.[0]?.toUpperCase() || (lang === 'ua' ? 'СІЛЬПО' : 'SILPO');
  const currencySymbol = config?.currencySymbol || (config?.currency === 'UAH' ? '₴' : (lang === 'ua' ? '₴' : '$'));
  const budgetStr = config ? `${currencySymbol}${config.budgetLimitUSD.toFixed(2)}` : `${currencySymbol}100`;

  // S.T.A.L.K.E.R. Skin Tactical Copy
  if (skin === 'stalker') {
    return [
      {
        id: 'stalker-1',
        type: 'thought',
        thought: '[PDA_TELEMETRY] Сканування біометричних сигнатур та потреб сталкера...',
        details: 'Ідентифіковано дефіцити білка та електролітів для виживання в радіаційному секторі.',
        badge: 'PDA_SYSTEM'
      },
      {
        id: 'stalker-2',
        type: 'skill',
        toolName: 'gemini-spark-zone-protocol',
        thought: 'Using skill: zone-survival-instructions',
        details: 'Калібрування евристичного алгоритму підбору безпечних схронів провізії.',
        badge: 'SKILL_ACTIVE'
      },
      {
        id: 'stalker-3',
        type: 'search',
        title: `Stash Registry Search: ${storeName}`,
        thought: `Сканування реєстру схронів та провізії в секторі ${storeName}...`,
        details: `Зіставлено каталог позицій та актуальні ціни в межах бюджету: ${budgetStr}.`,
        badge: 'SECTOR_SEARCH'
      },
      {
        id: 'stalker-4',
        type: 'verification',
        thought: '[RAD_FILTER] Верифікація Safe Choice та готовність PDA Push сповіщення...',
        details: 'Токсичних домішок не виявлено. Кошик санкціоновано для передачі на PDA.',
        badge: 'RAD_CLEAR'
      }
    ];
  }

  // Ukrainian Localization
  if (lang === 'ua') {
    const isOnlyGrocery = config?.groceryListMode === 'only_grocery';
    const isIncludeGrocery = config?.groceryListMode === 'include_grocery';
    const groceryCount = config?.groceryItems?.length || 0;

    if (vibe === 'casual') {
      return [
        {
          id: 'ua-1',
          type: 'thought',
          thought: isOnlyGrocery 
            ? `Синхронізую ${groceryCount} товарів зі списку покупок додатку...`
            : isIncludeGrocery
            ? `Об'єдную список покупок (${groceryCount} тов.) та денні макро-дефіцити...`
            : 'Аналізую денний раціон, дефіцити білка та список покупок...',
          details: isOnlyGrocery
            ? 'Перевірено актуальний список продуктів з вашого додатку для точного викупу.'
            : 'Визначив пріоритетні продукти для закриття дефіцитів нутрієнтів з максимальною користю.',
          badge: 'Аналіз'
        },
        {
          id: 'ua-2',
          type: 'skill',
          toolName: 'gemini-spark-instructions',
          thought: 'Using skill: nutri-cart-optimizer',
          details: 'Застосовую ШІ-модель оптимізації цін, білкового балансу та складу товарів.',
          badge: 'ШІ Інструмент'
        },
        {
          id: 'ua-3',
          type: 'search',
          title: `Store Catalog Search: ${storeName}`,
          thought: `Пошук товарів та перевірка актуальних цін у ${storeName}...`,
          details: `Товари в наявності. Підсумок вкладається у бюджет (${budgetStr}).`,
          badge: 'Каталог'
        },
        {
          id: 'ua-4',
          type: 'verification',
          thought: 'Перевірка Safe Choice, алергенів та формування Push-сповіщення...',
          details: 'Кошик відповідає дієтичному профілю. Сповіщення надіслано в додаток.',
          badge: 'Safe Choice'
        }
      ];
    }

    // Ukrainian Professional
    return [
      {
        id: 'ua-pro-1',
        type: 'thought',
        thought: isOnlyGrocery
          ? `Ініціалізація списку покупок: верифікація ${groceryCount} позицій користувача...`
          : 'Ініціалізація аналітичного модуля: оцінка добового балансу та дефіцитів...',
        details: isOnlyGrocery
          ? 'Сформовано точний перелік для безпомилкового викупу у партнерській мережі.'
          : 'Сформовано перелік пріоритетних нутрієнтів з акцентом на покриття білкових потреб.',
        badge: 'Аналітика'
      },
      {
        id: 'ua-pro-2',
        type: 'skill',
        toolName: 'gemini-spark-instructions',
        thought: 'Using skill: nutri-cart-optimizer',
        details: 'Активація математичної моделі балансування вартості кошика та нутрієнтної щільності.',
        badge: 'Алгоритм'
      },
      {
        id: 'ua-pro-3',
        type: 'search',
        title: `Store Catalog Search: ${storeName}`,
        thought: `Запит до бази даних цін та залишків ${storeName}...`,
        details: `Позиції верифіковано. Загальна вартість відповідає ліміту ${budgetStr}.`,
        badge: 'Склад'
      },
      {
        id: 'ua-pro-4',
        type: 'verification',
        thought: 'Валідація складу за протоколом Safe Choice та готовність Push-сигналу...',
        details: 'Алергенних ризиків не виявлено. Пакет підготовлено до санкціонування.',
        badge: 'Верифікація'
      }
    ];
  }

  // English Default
  const isOnlyGroceryEn = config?.groceryListMode === 'only_grocery';
  const isIncludeGroceryEn = config?.groceryListMode === 'include_grocery';
  const groceryCountEn = config?.groceryItems?.length || 0;

  return [
    {
      id: 'en-1',
      type: 'thought',
      thought: isOnlyGroceryEn 
        ? `Syncing ${groceryCountEn} items from your active app grocery list...`
        : isIncludeGroceryEn
        ? `Combining app grocery list (${groceryCountEn} items) with macro deficit targets...`
        : 'Analyzing nutrient logs, protein gaps, and active grocery needs...',
      details: isOnlyGroceryEn
        ? 'Verified your app shopping list for precision store fulfillment.'
        : 'Identified target macro requirements and priority items from your recent profile.',
      badge: 'Analysis'
    },
    {
      id: 'en-2',
      type: 'skill',
      toolName: 'gemini-spark-instructions',
      thought: 'Using skill: nutri-cart-optimizer',
      details: 'Applying AI heuristic model to maximize nutrient density per dollar spent.',
      badge: 'AI Skill'
    },
    {
      id: 'en-3',
      type: 'search',
      title: `Store Catalog Search: ${storeName}`,
      thought: `Querying live stock and pricing from ${storeName}...`,
      details: `In-stock confirmation received within your budget ceiling (${budgetStr}).`,
      badge: 'Store Search'
    },
    {
      id: 'en-4',
      type: 'verification',
      thought: 'Safe Choice verification & preparing App Push notification...',
      details: 'Zero allergen conflicts detected. Order ready for 1-click approval.',
      badge: 'Safe Choice'
    }
  ];
};

export const NutriAgentProgress: React.FC<NutriAgentProgressProps> = ({
  isActive,
  config,
  onComplete,
  onCancel,
  lang = 'en-US',
  vibe = 'casual',
  theme = 'dark',
  skin = 'default'
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(true);
  const [expandedDetails, setExpandedDetails] = useState<Record<string, boolean>>({});
  const onCompleteRef = useRef(onComplete);
  const timelineEndRef = useRef<HTMLDivElement>(null);

  const isUa = lang === 'ua';
  const isStalker = skin === 'stalker';
  const isCyberpunk = skin === 'cyberpunk';
  const isAnime = skin === 'anime';

  const timelineActions = getTimelineActions(config, lang, vibe, skin);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const toggleDetail = (id: string) => {
    setExpandedDetails(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    if (timelineEndRef.current) {
      timelineEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [currentStep, expandedDetails]);

  useEffect(() => {
    if (!isActive) {
      setCurrentStep(0);
      setExpandedDetails({});
      return;
    }

    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < timelineActions.length - 1) {
          return prev + 1;
        } else {
          clearInterval(interval);
          
          // Trigger actual API call if config is available
          if (config) {
            const isUaCurrency = config.currency === 'UAH';
            const currencySymbol = config.currencySymbol || (isUaCurrency ? '₴' : '$');
            fetch('/api/nutri-agent/plan-cart', {
              method: 'POST',
              headers: {
                 'Content-Type': 'application/json',
                 'Authorization': 'Bearer ' + localStorage.getItem('accessToken') 
              },
              body: JSON.stringify(config)
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                  const storeUpper = (config.targetStores[0] || 'SILPO').toUpperCase();
                  const isSilpo = storeUpper === 'SILPO' || config.currency === 'UAH';

                  const handleDispatch = (syncResult?: any) => {
                    const finalItems = syncResult?.addedDetails?.length > 0 ? syncResult.addedDetails.map((d: any) => ({
                      name: d.name,
                      weight: d.ratio || '1 unit',
                      price: d.price,
                      qty: d.quantity || 1
                    })) : data.items;

                    return fetch('/api/nutri-agent/dispatch', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        payload: {
                          storeName: config.targetStores[0] ? config.targetStores[0].toUpperCase() : 'SILPO',
                          currency: config.currency || 'USD',
                          currencySymbol: currencySymbol,
                          items: finalItems,
                          subtotal: data.subtotal,
                          deliveryFee: data.deliveryFee,
                          totalSum: syncResult?.totalAfterDiscounts ?? data.totalSum,
                          deliverySlot: isUaCurrency ? 'Сьогодні, 16:30 - 18:00' : 'Today, 4:30 PM - 6:00 PM',
                          selectedCard: 'Direct Store Checkout',
                          userNotes: config.userNotes,
                          taskDefinition: { budgetLimitUSD: config.budgetLimitUSD, currency: config.currency }
                        },
                        chatHandle: 'app_push'
                      })
                    }).catch(err => console.warn('[Nutri-Agent] API Dispatch error:', err));
                  };

                  if (isSilpo) {
                    fetch('/api/nutri-agent/sync-silpo-cart', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ items: data.items })
                    })
                      .then(r => r.json())
                      .then(syncRes => handleDispatch(syncRes))
                      .catch(e => {
                        console.warn('[Nutri-Agent Silpo Sync error]:', e);
                        handleDispatch();
                      });
                  } else {
                    handleDispatch();
                  }
                }
            })
            .catch(err => console.warn('[Nutri-Agent] API Dispatch error:', err));
          }

          setTimeout(() => {
            onCompleteRef.current();
          }, 1200);
          return prev;
        }
      });
    }, 1500); // 1.5s step interval

    return () => clearInterval(interval);
  }, [isActive, timelineActions.length]);

  if (!isActive || !config) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[95] flex items-center justify-center p-3 sm:p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={cn(
            "absolute inset-0 backdrop-blur-md transition-all",
            isStalker ? "bg-black/90" : "bg-black/75"
          )}
        />

        {/* Modal Container - Fixed sleek height to prevent enlarging */}
        <motion.div
          initial={{ scale: 0.94, opacity: 0, y: 16 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.94, opacity: 0, y: 16 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          className={cn(
            "relative w-full max-w-md h-[500px] max-h-[85vh] overflow-hidden rounded-[28px] border shadow-2xl flex flex-col",
            isStalker
              ? "bg-[#000000] border-[#4ade80]/60 shadow-[0_0_50px_rgba(74,222,128,0.2)] font-mono text-[#4ade80]"
              : isCyberpunk
                ? "bg-[#050510] border-[#FCEE0A]/50 shadow-[0_0_40px_rgba(252,238,10,0.25)] rounded-none text-[#00ffcc]"
                : isAnime
                  ? "bg-gradient-to-b from-[#1c1438]/95 to-[#120e28]/95 border-pink-400/40 shadow-[0_0_50px_rgba(236,72,153,0.3)] text-pink-100"
                  : theme === 'light'
                    ? "bg-white border-zinc-200 shadow-[0_20px_60px_rgba(0,0,0,0.12)] text-zinc-900"
                    : "bg-zinc-950 border-zinc-800 shadow-[0_20px_60px_rgba(0,0,0,0.6)] text-zinc-100"
          )}
        >
          {/* Top Header */}
          <div className={cn(
            "p-4 sm:p-5 pb-3 border-b flex items-center justify-between shrink-0",
            isStalker
              ? "border-[#4ade80]/30 bg-black/80"
              : isCyberpunk
                ? "border-[#FCEE0A]/20 bg-[#050510]/80 rounded-none"
                : isAnime
                  ? "border-pink-500/30 bg-pink-950/30"
                  : theme === 'light'
                    ? "border-zinc-100 bg-zinc-50/70"
                    : "border-zinc-800/80 bg-zinc-900/40"
          )}>
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="relative shrink-0">
                <div className={cn(
                  "absolute inset-0 rounded-full animate-ping opacity-30",
                  isStalker ? "bg-[#4ade80]" : isAnime ? "bg-pink-500" : isCyberpunk ? "bg-[#FCEE0A]" : "bg-[#10B981]"
                )} />
                <div className={cn(
                  "relative w-9 h-9 rounded-xl flex items-center justify-center border",
                  isStalker
                    ? "bg-[#4ade80]/10 border-[#4ade80]/40 text-[#4ade80]"
                    : isAnime
                      ? "bg-pink-500/20 border-pink-400/40 text-pink-300 shadow-[0_0_10px_rgba(236,72,153,0.4)]"
                      : isCyberpunk
                        ? "bg-[#FCEE0A]/15 border-[#FCEE0A] text-[#FCEE0A] rounded-none shadow-[0_0_10px_rgba(252,238,10,0.5)]"
                        : "bg-emerald-500/10 border-emerald-500/30 text-[#10B981]"
                )}>
                  <Bot className="w-4 h-4" />
                </div>
              </div>
              <div className="min-w-0">
                <h2 className={cn(
                  "text-sm sm:text-base font-black uppercase tracking-wider truncate",
                  isStalker ? "text-[#4ade80]" : isAnime ? "text-pink-300 drop-shadow-[0_0_8px_rgba(236,72,153,0.5)]" : isCyberpunk ? "text-[#FCEE0A] drop-shadow-[0_0_8px_rgba(252,238,10,0.6)] font-mono" : "text-[var(--text)]"
                )}>
                  {isStalker
                    ? 'PDA TACTICAL AGENT'
                    : isCyberpunk
                      ? 'CYBERPUNK NEURAL AGENT'
                      : isAnime
                        ? (isUa ? '『НУТРІ-АГЕНТ: КВЕСТ』 🌸' : '『AI NUTRI-AGENT』 🌸')
                        : isUa
                          ? (vibe === 'casual' ? 'Нутрі-Агент у Дії ⚡' : 'Автономний Нутрі-Агент')
                          : 'Nutri-Agent Live Runtime'}
                </h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={cn(
                    "text-[9px] font-black uppercase tracking-widest px-1.5 py-0.2 rounded-md border",
                    isStalker
                      ? "bg-[#4ade80]/10 border-[#4ade80]/30 text-[#4ade80]"
                      : isAnime
                        ? "bg-pink-500/20 border-pink-400/40 text-pink-300 shadow-[0_0_8px_rgba(236,72,153,0.3)]"
                        : isCyberpunk
                          ? "bg-[#FCEE0A] border-[#FCEE0A] text-black font-mono rounded-none font-black"
                          : "bg-emerald-500/10 border-emerald-500/20 text-[#10B981]"
                  )}>
                    {isStalker ? 'ZONE.ONLINE' : isCyberpunk ? 'LIVE' : isAnime ? 'QUEST.ACTIVE ✨' : (isUa ? 'АВТО-ЗАМОВЛЕННЯ' : 'SMART FULFILLMENT')}
                  </span>
                  <span className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400 font-mono">
                    {currentStep + 1}/{timelineActions.length}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={onCancel}
              className={cn(
                "px-2.5 py-1 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all border shrink-0 cursor-pointer",
                isStalker
                  ? "bg-red-950/40 text-red-400 border-red-500/40 hover:bg-red-900/60"
                  : isCyberpunk
                    ? "bg-[#050510] border-[#FCEE0A]/40 text-[#FCEE0A] hover:bg-[#FCEE0A]/20 rounded-none"
                    : theme === 'light'
                      ? "bg-zinc-100 hover:bg-red-50 text-zinc-500 hover:text-red-500 border-zinc-200"
                      : "bg-zinc-900 hover:bg-red-950/40 text-zinc-400 hover:text-red-400 border-zinc-800"
              )}
            >
              {isStalker ? '[ABORT]' : (isUa ? 'Скасувати' : 'Cancel')}
            </button>
          </div>

          {/* Collapsible Accordion Header "Thinking it through ^" */}
          <div className="px-4 sm:px-5 pt-2.5 pb-1 shrink-0">
            <button
              onClick={() => setIsTimelineExpanded(!isTimelineExpanded)}
              className={cn(
                "w-full flex items-center justify-between py-1.5 px-3 rounded-xl transition-all text-xs font-bold select-none cursor-pointer border",
                isStalker
                  ? "bg-black text-[#4ade80] border-[#4ade80]/30 hover:bg-[#4ade80]/10"
                  : isAnime
                    ? "bg-pink-950/20 text-pink-300 border-pink-500/20 hover:bg-pink-950/40"
                    : isCyberpunk
                      ? "bg-cyan-950/20 text-cyan-300 border-cyan-500/20 hover:bg-cyan-950/40"
                      : theme === 'light'
                        ? "bg-zinc-100/70 hover:bg-zinc-200/60 text-zinc-700 border-zinc-200/80"
                        : "bg-zinc-900/60 hover:bg-zinc-800/60 text-zinc-300 border-zinc-800/80"
              )}
            >
              <div className="flex items-center gap-1.5">
                <Sparkles className={cn(
                  "w-3.5 h-3.5 animate-pulse",
                  isStalker ? "text-[#4ade80]" : isAnime ? "text-pink-400" : isCyberpunk ? "text-cyan-400" : "text-[#10B981]"
                )} />
                <span className="font-extrabold text-[11px] tracking-tight">
                  {isStalker
                    ? 'PDA THOUGHT PROTOCOL'
                    : isUa
                      ? 'Хід думок агента'
                      : 'Thinking it through'}
                </span>
                <span className={cn(
                  "text-[8px] font-black px-1 py-0.1 rounded-full uppercase tracking-wider",
                  isStalker ? "bg-[#4ade80]/20 text-[#4ade80]" : "bg-emerald-500/10 text-emerald-500"
                )}>
                  Live
                </span>
              </div>
              <div className="flex items-center gap-1 text-zinc-400">
                <span className="text-[10px] font-mono">
                  {isTimelineExpanded ? (isUa ? 'Згорнути' : 'Collapse') : (isUa ? 'Розгорнути' : 'Expand')}
                </span>
                {isTimelineExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </div>
            </button>
          </div>

          {/* Timeline Scrollable Content Area - Fixed height viewport */}
          <div className="flex-1 overflow-y-auto no-scrollbar p-4 sm:p-5 space-y-3 min-h-0">
            <AnimatePresence initial={false}>
              {isTimelineExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="relative pl-5 space-y-3.5"
                >
                  {/* Vertical Connecting Line on Left */}
                  <div className={cn(
                    "absolute left-[9px] top-2.5 bottom-2.5 w-[2px]",
                    isStalker
                      ? "bg-[#4ade80]/30 shadow-[0_0_8px_rgba(74,222,128,0.4)]"
                      : isAnime
                        ? "bg-pink-500/30"
                        : isCyberpunk
                          ? "bg-cyan-500/30"
                          : theme === 'light'
                            ? "bg-zinc-200"
                            : "bg-zinc-800"
                  )} />

                  {/* Render steps sequentially up to currentStep */}
                  {timelineActions.map((action, index) => {
                    const isCompleted = index < currentStep;
                    const isCurrent = index === currentStep;
                    const isPending = index > currentStep;
                    const isDetailOpen = !!expandedDetails[action.id];

                    if (isPending) return null;

                    return (
                      <motion.div
                        key={action.id}
                        initial={{ opacity: 0, x: -8, y: 3 }}
                        animate={{ opacity: 1, x: 0, y: 0 }}
                        transition={{ duration: 0.25 }}
                        className="relative group"
                      >
                        {/* Left Node Dot */}
                        <div className={cn(
                          "absolute -left-[26px] top-1 w-5 h-5 rounded-full flex items-center justify-center border transition-all z-10",
                          isCompleted
                            ? (isStalker 
                                ? "bg-black border-[#4ade80] text-[#4ade80]" 
                                : isAnime
                                  ? "bg-[#0A0B10] border-pink-500 text-pink-400"
                                  : "bg-white dark:bg-zinc-950 border-[#10B981] text-[#10B981]")
                            : isCurrent
                              ? (isStalker
                                  ? "bg-[#4ade80]/20 border-[#4ade80] text-[#4ade80] animate-pulse shadow-[0_0_10px_#4ade80]"
                                  : isAnime
                                    ? "bg-pink-500/20 border-pink-400 text-pink-400 animate-pulse shadow-[0_0_10px_#ec4899]"
                                    : "bg-emerald-500/20 border-emerald-500 text-[#10B981] animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.5)]")
                              : "bg-zinc-800 border-zinc-700 text-zinc-500"
                        )}>
                          {isCompleted ? (
                            <CheckCircle2 className="w-3 h-3" />
                          ) : isCurrent ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Clock className="w-2.5 h-2.5" />
                          )}
                        </div>

                        {/* Action Card */}
                        <div className={cn(
                          "p-2.5 sm:p-3 rounded-xl border transition-all space-y-1.5",
                          isCurrent
                            ? (isStalker
                                ? "bg-[#4ade80]/10 border-[#4ade80]/50 shadow-xs"
                                : isAnime
                                  ? "bg-pink-950/20 border-pink-500/40 shadow-xs"
                                  : isCyberpunk
                                    ? "bg-cyan-950/20 border-cyan-500/40 shadow-xs"
                                    : theme === 'light'
                                      ? "bg-zinc-50 border-[#10B981]/40 shadow-xs ring-1 ring-[#10B981]/20"
                                      : "bg-zinc-900/70 border-[#10B981]/40 shadow-xs ring-1 ring-[#10B981]/20")
                            : (theme === 'light'
                                ? "bg-white/80 border-zinc-100 text-zinc-600"
                                : "bg-zinc-900/30 border-zinc-800/60 text-zinc-400")
                        )}>
                          {/* Skill Badge */}
                          {action.type === 'skill' && (
                            <div className="flex items-center gap-1.5">
                              <Wrench className={cn(
                                "w-3 h-3 shrink-0",
                                isStalker ? "text-[#4ade80]" : isAnime ? "text-pink-400" : isCyberpunk ? "text-cyan-400" : "text-[#10B981]"
                              )} />
                              <span className={cn(
                                "text-[11px] font-black tracking-tight",
                                isCurrent 
                                  ? (isStalker ? "text-[#4ade80]" : isAnime ? "text-pink-300" : isCyberpunk ? "text-cyan-200" : "text-[var(--text)] font-extrabold")
                                  : "text-zinc-400"
                              )}>
                                {action.thought}
                              </span>
                            </div>
                          )}

                          {/* Search Title */}
                          {action.type === 'search' && (
                            <div className="flex items-center gap-1.5">
                              <div className="p-0.5 rounded-md bg-emerald-500/10 text-[#10B981]">
                                <Search className="w-3 h-3" />
                              </div>
                              <span className={cn(
                                "text-[11px] font-black tracking-tight",
                                isCurrent ? "text-[var(--text)] font-extrabold" : "text-zinc-400"
                              )}>
                                {action.title || action.thought}
                              </span>
                            </div>
                          )}

                          {/* Verification Title */}
                          {action.type === 'verification' && (
                            <div className="flex items-center gap-1.5">
                              <div className="p-0.5 rounded-md bg-emerald-500/10 text-[#10B981]">
                                <ShieldCheck className="w-3 h-3" />
                              </div>
                              <span className={cn(
                                "text-[11px] font-black tracking-tight",
                                isCurrent ? "text-[var(--text)] font-extrabold" : "text-zinc-400"
                              )}>
                                {action.thought}
                              </span>
                            </div>
                          )}

                          {/* Standard Thought Narrative */}
                          {action.type === 'thought' && (
                            <p className={cn(
                              "text-[11px] leading-relaxed font-medium",
                              isCurrent
                                ? (isStalker ? "text-[#4ade80] font-bold" : "text-[var(--text)] font-semibold")
                                : (theme === 'light' ? "text-zinc-600" : "text-zinc-400")
                            )}>
                              {action.thought}
                            </p>
                          )}

                          {/* Expandable Sub-Details */}
                          {action.details && (
                            <div className="pt-0.5">
                              <AnimatePresence initial={false}>
                                {isDetailOpen && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.15 }}
                                    className="overflow-hidden mb-1.5"
                                  >
                                    <div className={cn(
                                      "p-2 rounded-lg text-[10px] leading-relaxed border",
                                      isStalker
                                        ? "bg-black border-[#4ade80]/20 text-[#4ade80]/90 font-mono"
                                        : theme === 'light'
                                          ? "bg-zinc-100/80 border-zinc-200 text-zinc-700"
                                          : "bg-zinc-950/60 border-zinc-800 text-zinc-300"
                                    )}>
                                      {action.details}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>

                              <button
                                type="button"
                                onClick={() => toggleDetail(action.id)}
                                className={cn(
                                  "inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider transition-colors cursor-pointer",
                                  isStalker
                                    ? "text-[#4ade80]/80 hover:text-[#4ade80]"
                                    : "text-zinc-400 hover:text-emerald-500 dark:hover:text-emerald-400"
                                )}
                              >
                                <span>{isDetailOpen ? (isUa ? 'Приховати' : 'Hide details') : (isUa ? 'Показати деталі' : 'Show details')}</span>
                                {isDetailOpen ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                  <div ref={timelineEndRef} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Bottom Store & Push Notification Footer */}
          <div className={cn(
            "p-3.5 px-4 sm:px-5 border-t flex items-center justify-between gap-2 text-xs shrink-0 font-mono",
            isStalker
              ? "border-[#4ade80]/30 bg-black/90 text-[#4ade80]"
              : isCyberpunk
                ? "border-cyan-500/20 bg-[#050510]"
                : isAnime
                  ? "border-pink-500/20 bg-[#0A0B10]"
                  : theme === 'light'
                    ? "border-zinc-100 bg-zinc-50/90 text-zinc-600"
                    : "border-zinc-800/80 bg-zinc-900/60 text-zinc-400"
          )}>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="font-bold flex items-center gap-1">
                <ShoppingCart className="w-3.5 h-3.5 text-emerald-500" />
                <span className="uppercase">{config.targetStores[0] || 'SILPO'}</span>
              </span>
              <span>•</span>
              <span className="font-bold text-emerald-500 dark:text-emerald-400">
                {config.currencySymbol || '$'}{config.budgetLimitUSD.toFixed(2)}
              </span>
            </div>

            <div className="flex items-center gap-1 text-emerald-500 text-[10px] font-bold uppercase tracking-wider">
              <Bell className="w-3 h-3" />
              <span>{isUa ? 'Push-сповіщення' : 'App Push Ready'}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

